import * as Tabs from '@radix-ui/react-tabs';
import { createContext, Fragment, ReactNode, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { theme as commonTheme } from '../common.theme';
import { TabNavigationContext } from './_tab_navigation.context';
import { theme } from './tab_navigation.theme';

export interface TabNavigationBarProps {
	/**
	 * Visually hidden label. It's used to inform assistive technologies, such as
	 * screen readers, about the context.
	 */
	label: string;

	/**
	 * Event handler called when a tab is clicked
	 */
	onClick?: (value: string) => void;
}

const elements = {
	wrapper: styled.div`
		height: 42px;

		position: relative;
        isolation: isolate;
		width: 100%;

		&::after {
			content: '';
			left: 0;
			right: 0;
			bottom: 2px;
			position: absolute;
			height: 2px;
			background-color: ${theme.getValue('sliderBackground')};
		}
	`,
	container: styled(Tabs.List)`
		position: relative;
		display: flex;
		height: 40px;
		width: 100%;
		overflow-x: auto;
		overflow-y: hidden;
		scroll-snap-type: x proximity;
		-webkit-overflow-scrolling: touch;
		padding-bottom: 2px;
		box-sizing: border-box;

		&::-webkit-scrollbar {
			display: none;
		}
	`,
	tab: styled(Tabs.Trigger)`
		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		width: 100%;
		transition: background-color 200ms ease-in-out color 150ms ease-in-out;
		padding: ${theme.getValue('triggerPadding')};
		color: ${theme.getValue('triggerPassiveColor')};
		position: relative;
		transition: color 150ms ease-in-out;
		text-overflow: ellipsis;
		touch-action: manipulation;
		scroll-snap-align: start;
		cursor: default;
		font: ${theme.getValue('triggerFont')};
		gap: ${coreTokens.spacing.x1_5};

		${coreTokens.breakpointQuery.medium} {
			justify-content: start;
			width: auto;
		}

		&:focus-visible {
			outline: 2px solid ${commonTheme.getValue('focusColor')};
			outline-offset: -2px;
			border-radius: 4px;
		}

		&[data-disabled] {
			cursor: not-allowed;
			opacity: 0.7;
		}

		&[data-state='active'] {
			& .dataText {
				font: ${theme.getValue('triggerActiveFont')};
				color: ${theme.getValue('triggerActiveColor')};
			}

			&::before {
				content: '';
				width: 100%;
				height: 2px;
				position: absolute;
				bottom: -2px;
				left: 0;
				background-color: ${theme.getValue('triggerActiveBackground')};
                z-index: 2
			}
		}

		& .dataText {
			display: inline-flex;
			flex-direction: column;
			align-items: center;
			justify-content: space-between;
			font: ${theme.getValue('triggerFont')};
			transition: color 150ms ease-in-out;

			/*
				To avoid layout shift, this pseudo-element is added.
			*/
			&::after {
				content: attr(data-text);
				content: attr(data-text) / '';
				height: 0;
				visibility: hidden;
				overflow: hidden;
				user-select: none;
				pointer-events: none;
				font: ${theme.getValue('triggerActiveFont')};

				@media speech {
					display: none;
				}
			}
		}

		@media (hover: hover) {
			&:hover:not([data-disabled]) .dataText {
				color: ${theme.getValue('triggerHoverColor')};
			}
		}
	`,
};

interface TabNavigationBarContextSpec {
	onClick?: (value: string) => void;
}

const TabNavigationBarContext = createContext<TabNavigationBarContextSpec>({} as unknown as TabNavigationBarContextSpec);

export function TabNavigationBar({ label, onClick }: TabNavigationBarProps): JSX.Element {
	const { tabs } = useContext(TabNavigationContext);

	return (
		<TabNavigationBarContext.Provider
			value={{
				onClick,
			}}
		>
			<elements.wrapper>
				<elements.container aria-label={label}>
					{tabs
						.filter((tab) => !tab.removed)
						.map((tab) => (
							<Fragment key={tab.value}>
								<TabItem value={tab.value} hiddenLabel={tab.hiddenLabel} trailingSlot={tab.trailingSlot} disabled={tab.disabled}>
									{tab.label}
								</TabItem>
							</Fragment>
						))}
				</elements.container>
			</elements.wrapper>
		</TabNavigationBarContext.Provider>
	);
}

// #region internals
interface TabProps {
	trailingSlot: ReactNode | null;
	hiddenLabel: string | null;
	value: string;
	children: ReactNode;
	disabled?: boolean;
}

/* @internal */
function TabItem({ value, hiddenLabel, trailingSlot, children, disabled }: TabProps): JSX.Element {
	const { onClick } = useContext(TabNavigationBarContext);

	const handleClick = useCallback(() => {
		onClick?.(value);
	}, [onClick, value]);

	return (
		<elements.tab value={value} onClick={handleClick} aria-label={hiddenLabel ?? undefined} disabled={disabled}>
			<div className='dataText' data-text={children}>
				{children}
			</div>

			{trailingSlot != null ? trailingSlot : null}
		</elements.tab>
	);
}
// #endregion
